import { taskListOptions } from "@dashboard/api/queryApi";
import {
    createTaskList,
    updateTaskList,
    updateTaskListWithDetails,
    deleteTaskList,
    type CreateTaskForListInput,
    type CreateTaskListInput,
    type CreateTaskListWithDetailsResponse,
    type TaskListWithTaskOccurrences,
    type TaskWithOccurrences,
    type UpdateTaskListInput,
    type UpdateTaskListWithDetailsInput,
    deleteTaskGroup,
} from "@dashboard/api/task";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

function invalidateListQueries(queryClient: QueryClient, listId?: number) {
    queryClient.invalidateQueries({ queryKey: ["listsWithTasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskListWithTasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskLists"] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskGroups"] });
    queryClient.invalidateQueries({ queryKey: ["taskOccurrences"] });

    if (listId) {
        queryClient.invalidateQueries(taskListOptions(listId));
    }
}

export function useCreateList() {
    const queryClient = useQueryClient();
    return useMutation<CreateTaskListWithDetailsResponse, Error, CreateTaskListInput>({
        mutationFn: (data: CreateTaskListInput) => createTaskList(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["taskLists"] });
            queryClient.invalidateQueries({ queryKey: ["tasks"] });
            queryClient.invalidateQueries({ queryKey: ["taskGroups"] });
            queryClient.invalidateQueries({ queryKey: ["listsWithTasks"] });
        },
    });
}

export function useUpdateList() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateTaskListWithDetailsInput }) =>
            updateTaskListWithDetails(id, data),
        onSuccess: (_, variables) => {
            invalidateListQueries(queryClient, variables.id);
        },
    });
}

export function usePatchList() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateTaskListInput }) =>
            updateTaskList(id, data),
        onSuccess: (_, variables) => {
            invalidateListQueries(queryClient, variables.id);
        },
    });
}

export function useDeleteList() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => deleteTaskList(id),
        onSuccess: (_, deletedId) => {
            invalidateListQueries(queryClient, deletedId);
        },
    });
}

export function useDeleteGroup() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => deleteTaskGroup(id),
        onSuccess: () => {
            invalidateListQueries(queryClient);
        },
    });
}

export function taskToCopyInput(t: TaskWithOccurrences): CreateTaskForListInput {
    return {
        title: t.title ?? "",
        description: t.description,
        sourceApp: t.sourceApp ?? undefined,
        action: t.action ?? undefined,
        annualCycle: t.annualCycle,
        ownerUserId: t.ownerUserId ?? undefined,
        status: t.status,
        rrule: t.rrule,
        duration: t.duration ?? undefined,
    };
}

function listToCreateInput(
    list: TaskListWithTaskOccurrences,
    copySuffix: string,
): CreateTaskListInput {
    return {
        instanceId: list.instanceId,
        title: `${list.title} (${copySuffix})`,
        description: list.description,
        tasks: list.tasks.map(taskToCopyInput),
        groups: list.groups.map((g) => ({
            title: g.title,
            tasks: g.tasks.map(taskToCopyInput),
        })),
    };
}

export function useCopyList() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation<CreateTaskListWithDetailsResponse, Error, TaskListWithTaskOccurrences>({
        mutationFn: (list: TaskListWithTaskOccurrences) => {
            const input = listToCreateInput(list, t("list.copy"));
            return createTaskList(input);
        },
        onSuccess: () => {
            invalidateListQueries(queryClient);
        },
    });
}
